import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  extend,
  params,
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { useNavigate } from '@thd-olt-component-react/router';
import { ResultsDimensions } from '../components/ResultsDimensions';
import { ProductResultsSearchMethodsPLP } from '../utils/product-results-search-methods-PLP';
import { ProductResultsSearchMethodsPEP } from '../utils/product-results-search-methods-PEP';
import { usePlpResultsHeaderReadyEvent } from '../hooks/usePlpResultsHeaderReadyEvent';

const ResultsDimensionsComposable = ({ type, hideFFMFilters }) => {
  // TODO: Refactor consumer components to remove the dead code
  // these have been enabled long enough, making the fallback code unreachable
  const isGetItFastOnPlp = true;
  const viewAllFilterButton = useConfigService('fs-prop:isViewAllFilterButtonEnabled');
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const ctx = useContext(ExperienceContext);
  const { customContext, channel } = ctx;
  const { variables={}, pageType } = customContext || {};
  const defaultOptions = {
    variables,
    ssr: true,
    skip: !variables.navParam && !variables.keyword,
  };
  const { data } = useDataModel('searchModel', defaultOptions);
  const { metadata } = data?.searchModel || {};
  const { contentType } = metadata || {};
  const isCategory = contentType === 'categorypage';
  const isDynamicBrandPage = data?.searchModel?.aisles?.length > 0;
  const navigate = useNavigate();
  const { onDimensionsChangePLP } = ProductResultsSearchMethodsPLP();
  const { onDimensionsChangePEP } = ProductResultsSearchMethodsPEP();
  const scrollTargetRef = usePlpResultsHeaderReadyEvent();

  if (isCategory || hideFFMFilters) {
    return null;
  }

  // Helper function to get wrapper classes based on type and conditions
  const getWrapperConfig = () => {
    if (channel === 'mobile' && type === 'horizontal' && isGetItFastOnPlp) {
      return {
        className: 'sui-py-2',
        testId: 'result-dimension-mobile'
      };
    }

    if (type === 'vertical' && !isGetItFastOnPlp) {
      return {
        className: 'sui-col-span-12 sm:sui-col-span-4 md:sui-col-span-3 lg:sui-col-span-12'
      };
    }

    if (type === 'horizontal' && isGetItFastOnPlp && viewPrimaryFilters) {
      return {
        className: 'sui-py-3'
      };
    }
    return null;
  };

  // Helper function to get component props based on type and conditions
  const getComponentProps = () => {
    const commonProps = {
      onDimensionsChange: (dimensionParams) => {
        if(pageType === 'pep'){
          return onDimensionsChangePEP({ ...dimensionParams, scrollTargetRef });
        }
        return onDimensionsChangePLP({ ...dimensionParams, viewPrimaryFilters, navigate });
      },
      enableMultiStore: true,
      dataProp: data,
    };

    if (channel === 'mobile' && type === 'horizontal' && isGetItFastOnPlp) {
      return {
        ...commonProps,
        showOnlyGetItFast: true,
        viewPrimaryFilters,
        isGetItFastOnPlp,
      };
    }

    if (type === 'vertical' && !isGetItFastOnPlp) {
      return {
        ...commonProps,
        showOnlyGetItFast: viewAllFilterButton && viewPrimaryFilters,
        shiftCategoryDimension: isDynamicBrandPage,
      };
    }

    if (type === 'horizontal' && isGetItFastOnPlp && viewPrimaryFilters) {
      return {
        ...commonProps,
        showOnlyGetItFast: true,
        isGetItFastOnPlp,
        viewPrimaryFilters,
      };
    }

    return null;
  };

  const wrapperConfig = getWrapperConfig();
  const componentProps = getComponentProps();

  // Return null if no valid configuration found
  if (!wrapperConfig || !componentProps) {
    return null;
  }

  return (
    <div
      className={wrapperConfig.className}
      data-testid={wrapperConfig.testId}
    >
      <ResultsDimensions
        onDimensionsChange={componentProps.onDimensionsChange}
        enableMultiStore={componentProps.enableMultiStore}
        showOnlyGetItFast={componentProps.showOnlyGetItFast}
        viewPrimaryFilters={componentProps.viewPrimaryFilters}
        isGetItFastOnPlp={componentProps.isGetItFastOnPlp}
        shiftCategoryDimension={componentProps.shiftCategoryDimension}
        dataProp={componentProps.dataProp}
      />
    </div>
  );
};

ResultsDimensionsComposable.propTypes = {
  type: PropTypes.string,
  hideFFMFilters: PropTypes.bool,
};

ResultsDimensionsComposable.defaultProps = {
  type: 'horizontal',
  hideFFMFilters: false,
};

ResultsDimensionsComposable.dataModel = extend({
  searchModel: params({
    keyword: stringType(),
    navParam: stringType(),
    storeId: stringType(),
    dynamicHydration: boolType(),
    dynamicHydrationPageSize: numberType(),
  }).shape({
    products: arrayType(shapeType({
      itemId: stringType()
    })),
    dimensions: arrayType(shapeType({
      label: stringType(),
      refinements: arrayType(shapeType({
        imgUrl: stringType(),
        label: stringType(),
        recordCount: stringType(),
        refinementKey: stringType(),
        url: stringType()
      })),
      collapse: boolType(),
      dimensionId: stringType(),
      isVisualNav: boolType(),
      nestedRefinementsLimit: numberType(),
      visualNavSequence: numberType()
    })),
    metadata: shapeType({
      canonicalUrl: stringType(),
      clearAllRefinementsURL: stringType(),
      contentType: stringType(),
      h1Tag: stringType(),
      isStoreDisplay: stringType(),
      productCount: shapeType({
        inStore: stringType(),
      }),
      stores: shapeType({
        storeId: stringType(),
        storeName: stringType(),
        address: shapeType({
          postalCode: stringType()
        }),
        nearByStores: arrayType(shapeType({
          storeId: stringType(),
          storeName: stringType()
        }))
      }),
    }),
  }),
});

ResultsDimensionsComposable.displayName = "ResultsDimensionsComposable";

export { ResultsDimensionsComposable };